"use client"

import { config } from "@/config"
import { LAUNCHER_APP_PATH } from "@/config/defaults"
import { showNotification } from "@/lib/notifications"
import { syncStore$ } from "@/stores/sync.v2.store"
import { Anchor, Box, Button, PasswordInput, rem, Stack, Text, TextInput, Title } from "@mantine/core"
import { useForm } from "@mantine/form"
import { makeURL } from "@nauverse/make-url"
import { IconLogin } from "@tabler/icons-react"
import { parseAsString, useQueryState } from "nuqs"
import { useEffect, useRef, useState } from "react"
import { z } from "zod"
import { chatStore$ } from "@/lib/ContactsMap"
import Link from "next/link"
import classes from "./LoginBox.module.css"

const loginURL = makeURL(config.environment.api.launcherURL ?? config.environment.api.baseURL, `${LAUNCHER_APP_PATH}/login/web`, {
  config: {
    trailingSlash: "remove",
  },
  params: {
    resource: "next",
  },
})

export function LoginBox() {
  const formRef = useRef<HTMLFormElement>(null)
  const [errorMessage, setErrorMessage] = useQueryState("message", parseAsString.withDefault(""))
  const [resultMessage, setResultMessage] = useQueryState("result", parseAsString.withDefault(""))
  const [csrfToken, setCsrfToken] = useState("")

  const [isLoading, setIsLoading] = useState(false)
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value) => {
        const safeEmail = z.string().email().safeParse(value)
        if (!safeEmail.success) {
          return "Invalid email"
        }
        return null
      },
      password: (value) => {
        const safePassword = z.string().trim().min(1).safeParse(value)
        if (!safePassword.success) {
          return "Password must have some characters"
        }
        return null
      },
    },
  })

  async function handleLogin() {
    if (isLoading) return
    form.validate()

    if (form.isValid() === false) return

    try {
      setIsLoading(true)

      formRef.current?.submit()
    } catch (err) {
      showNotification({
        type: "error",
        title: "Error while logging in",
        message: (err as any)?.message ?? "Try again in a few seconds or reach your administrator for help.",
        autoClose: 15000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (errorMessage.trim().length > 0 && resultMessage === "error") {
      showNotification({
        type: "error",
        title: "Error when trying to login",
        message: errorMessage,
        autoClose: 15000,
      })
      setErrorMessage(null)
      setResultMessage(null)
    }
    // Disabled because we don't want to run this effect on every render, only in the first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch CSRF token on mount
  useEffect(() => {
    const fetchCsrfToken = async () => {
      try {
        const response = await fetch("/api/csrf")
        const data = (await response.json()) as { csrfToken: string }
        setCsrfToken(data.csrfToken)
      } catch {
        setCsrfToken("")
      }
    }
    fetchCsrfToken()
  }, [])

  // Run only once, at the beginning
  // Clears the sync store
  useEffect(() => {
    chatStore$.chats.set([])
    chatStore$.chatLastInteraction.set({})
    chatStore$.liveCalls.set([])
    chatStore$.searchChats.set([])
    chatStore$.filteredChats.set([])
    chatStore$._internal.chatsMap.set({})
    chatStore$._internal.liveCallsMap.set({})
    chatStore$._internal.searchChatsMap.set({})
    syncStore$.clear()
  }, [])

  return (
    <>
      <Title order={1} size="h3" ta="center" data-testid="auth-login-title">
        Sign in to your account
      </Title>
      <Box
        mt={"lg"}
        className={classes.formContainer}
        data-testid="auth-login-form-container"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault()
            handleLogin()
          }
        }}
      >
        <form ref={formRef} method="POST" action={loginURL} className={classes.formContainer} data-testid="auth-login-form">
          <input type="hidden" name="_token" value={csrfToken} />
          <TextInput disabled={isLoading} placeholder="yourmail@domain.com" key={form.key("email")} name={"email"} data-testid="auth-login-email-input" {...form.getInputProps("email")} />
          <PasswordInput disabled={isLoading} placeholder="Password" key={form.key("password")} name={"password"} data-testid="auth-login-password-input" {...form.getInputProps("password")} />
          <Stack w="100%" gap={rem(6)}>
            <Button loading={isLoading} fullWidth leftSection={<IconLogin size={16} />} onClick={handleLogin} data-testid="auth-login-submit-button">
              Sign In
            </Button>
            <Anchor
              component={Link}
              href="/recover-password"
              style={{
                textAlign: "center",
                color: "var(--mantine-color-rBlue-9)",
              }}
              mt="xs"
              data-testid="auth-login-recover-password-link"
            >
              <Text size="sm">Forgot your password?</Text>
            </Anchor>
          </Stack>
        </form>
      </Box>
    </>
  )
}
